import React, { useState, useEffect } from 'react';
import { ServingSide } from '../../types';
import { PlayerAvatar } from '../atoms';
import { useTranslation } from '../../i18n/hooks/useTranslation';

// Hook pour détecter le mode landscape
function useIsLandscape() {
  const [isLandscape, setIsLandscape] = useState(
    typeof window !== 'undefined' ? window.innerWidth > window.innerHeight : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return isLandscape;
}

interface CourtProps {
  player: string;
  avatarUrl?: string;
  score: number;
  setScore: number;
  color: string;
  isServing: boolean;
  servingSide: ServingSide | null;
  onSideSelect: (side: ServingSide) => void;
  onScoreClick: () => void;
  canSelectSide: boolean;
  canChangeSide: boolean; // Contrôle la couleur de la balle (rouge si true, noir si false)
  canScore: boolean;
  onWarning: () => void;
  playerSide: 'A' | 'B';
  onColorClick?: () => void;
}

export function Court({
  player,
  avatarUrl,
  score,
  setScore,
  color,
  isServing,
  servingSide,
  onSideSelect,
  onScoreClick,
  canSelectSide,
  canChangeSide,
  canScore,
  onWarning,
  playerSide,
  onColorClick
}: CourtProps) {
  const { t } = useTranslation();
  const isLandscape = useIsLandscape();

  // En mode landscape : Player A a Decision à gauche, Player B à droite
  // En mode portrait : les deux ont Decision à droite (pour usage une main)
  const showDecisionOnLeft = isLandscape && playerSide === 'A';

  return (
    <div
      className={`relative flex flex-col h-full ${!isServing ? 'opacity-60' : ''}`}
      style={{ containerType: 'size' }}
    >
      {/* Header - 20% */}
      <div className="flex items-center justify-between h-[20%] w-full bg-white border-b border-gray-200">
        {showDecisionOnLeft ? (
          <>
            {/* Decision à gauche (Player A en landscape) */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onWarning();
              }}
              className="bg-black hover:bg-gray-800 text-white font-semibold px-3 ml-3 border-2 border-white transition-all h-[80%]"
              style={{ fontSize: 'clamp(0.7rem, 5cqh, 1.25rem)' }}
            >
              {t('decision')}
            </button>
            {/* Nom+Score+Icône à droite */}
            <button
              onClick={onColorClick}
              disabled={!onColorClick}
              className={`flex items-center gap-2 pr-2 h-full ${onColorClick ? 'cursor-pointer hover:bg-gray-50 transition-all' : ''}`}
            >
              <span className="font-semibold text-gray-900" style={{ fontSize: 'clamp(0.9rem, 6cqh, 2rem)' }}>{player}</span>
              <span
                className="bg-gray-100 text-gray-900 px-2 py-0.5 font-bold"
                style={{ fontSize: 'clamp(0.75rem, 6cqh, 1.75rem)' }}
              >
                {setScore}
              </span>
              <PlayerAvatar avatarUrl={avatarUrl} color={color} size="clamp(1.5rem, 10cqh, 3rem)" ringWidth={2} alt={player} />
            </button>
          </>
        ) : (
          <>
            {/* Icône+Score+Nom à gauche (Player B en landscape, ou les deux en portrait) */}
            <button
              onClick={onColorClick}
              disabled={!onColorClick}
              className={`flex items-center gap-2 pl-2 h-full ${onColorClick ? 'cursor-pointer hover:bg-gray-50 transition-all' : ''}`}
            >
              <PlayerAvatar avatarUrl={avatarUrl} color={color} size="clamp(1.5rem, 10cqh, 3rem)" ringWidth={2} alt={player} />
              <span
                className="bg-gray-100 text-gray-900 px-2 py-0.5 font-bold"
                style={{ fontSize: 'clamp(0.75rem, 6cqh, 1.75rem)' }}
              >
                {setScore}
              </span>
              <span className="font-semibold text-gray-900" style={{ fontSize: 'clamp(0.9rem, 6cqh, 2rem)' }}>{player}</span>
            </button>
            {/* Decision à droite */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onWarning();
              }}
              className="bg-black hover:bg-gray-800 text-white font-semibold px-3 mr-3 border-2 border-white transition-all h-[80%]"
              style={{ fontSize: 'clamp(0.7rem, 5cqh, 1.25rem)' }}
            >
              {t('decision')}
            </button>
          </>
        )}
      </div>

      {/* Zone court - 80% */}
      <div
        className="relative bg-gray-100 border-2 border-gray-300 p-2 flex flex-col justify-center h-[80%]"
      >
        {/* Grille score/côtés - 90% de la zone court */}
        <div className="grid grid-cols-4 items-stretch gap-2 h-[90%]">

          {/* Zone gauche - Côté gauche du terrain */}
          <button
            onClick={() => onSideSelect('left')}
            disabled={!canSelectSide}
            className={`col-span-1 bg-gray-200 border-2 relative transition-all ${
              canSelectSide
                ? 'border-gray-600 hover:bg-blue-100 hover:bg-opacity-30 cursor-pointer'
                : 'border-gray-300 cursor-not-allowed'
            }`}
          >
            {/* Balle gauche - visible si serveur ET côté gauche sélectionné */}
            {isServing && servingSide === 'left' && (
              <div className="absolute inset-0 flex items-center justify-center">
                {canChangeSide ? (
                  <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white font-bold transition-all duration-300" style={{ fontSize: '16px' }}>
                    ?
                  </div>
                ) : (
                  <div className="w-6 h-6 bg-black rounded-full transition-all duration-300"></div>
                )}
              </div>
            )}
          </button>
          
          {/* Score central */}
          <button
            onClick={onScoreClick}
            className={`col-span-2 font-bold border-2 flex items-center justify-center ${
              canScore
                ? 'bg-white border-gray-300 hover:bg-gray-50'
                : 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
            }`}
            style={{
              fontSize: 'clamp(2rem, 25cqh, 8rem)',
              color: canScore ? (isServing ? color : '#6B7280') : '#9CA3AF'
            }}
            disabled={!canScore}
          >
            {score}
          </button>
          
          {/* Zone droite - Côté droit du terrain */}
          <button
            onClick={() => onSideSelect('right')}
            disabled={!canSelectSide}
            className={`col-span-1 bg-gray-200 border-2 relative transition-all ${
              canSelectSide
                ? 'border-gray-600 hover:bg-blue-100 hover:bg-opacity-30 cursor-pointer'
                : 'border-gray-300 cursor-not-allowed'
            }`}
          >
            {/* Balle droite - visible si serveur ET (côté droit sélectionné OU aucun côté sélectionné) */}
            {isServing && (servingSide === 'right' || !servingSide) && (
              <div className="absolute inset-0 flex items-center justify-center">
                {canChangeSide ? (
                  <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white font-bold transition-all duration-300" style={{ fontSize: '16px' }}>
                    ?
                  </div>
                ) : (
                  <div className="w-6 h-6 bg-black rounded-full transition-all duration-300"></div>
                )}
              </div>
            )}
          </button>
          
        </div>

      </div>
    </div>
  );
}